"use client";

import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { LATEX_WORD_COUNTER_STRINGS } from "@/components/tools/latex-word-counter";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { TexCountResult } from "@/lib/tool-kit/latex-dev/texcount";

type Strings = typeof LATEX_WORD_COUNTER_STRINGS;

const PREVIEW_LIMIT = 20_000;
const TOP_WORDS = 15;
const TONES: Record<string, HighlightTone> = {
  text: "sky",
  header: "violet",
  other: "amber",
};

function summaryText(
  result: TexCountResult,
  strings: Strings,
  fmt: (n: number) => string
): string {
  return t(strings.summaryTemplate, {
    text: fmt(result.words),
    headers: fmt(result.headerWords),
    other: fmt(result.otherWords),
    total: fmt(result.words + result.headerWords + result.otherWords),
  });
}

export function LatexWordCounterResults({
  result,
  source,
  strings,
}: {
  result: TexCountResult;
  source: string;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const total = result.words + result.headerWords + result.otherWords;
  const matches: TextMatch[] = result.spans
    .filter((span) => span.end <= PREVIEW_LIMIT)
    .map((span) => ({
      start: span.start,
      end: span.end,
      category: span.kind,
    }));
  return (
    <div className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.wordsText}
          value={fmt(result.words)}
        />
        <StatCard
          label={strings.wordsHeaders}
          value={fmt(result.headerWords)}
        />
        <StatCard label={strings.wordsOther} value={fmt(result.otherWords)} />
        <StatCard label={strings.wordsTotal} value={fmt(total)} />
        <StatCard label={strings.sumLabel} value={fmt(result.sum)} />
        <StatCard label={strings.headers} value={fmt(result.headers)} />
        <StatCard label={strings.floats} value={fmt(result.floats)} />
        <StatCard label={strings.inlineMath} value={fmt(result.inlineMath)} />
        <StatCard label={strings.displayMath} value={fmt(result.displayMath)} />
      </StatGrid>
      <p className={HINT}>{strings.sumHint}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySummary}
        small
        text={summaryText(result, strings, fmt)}
      />
      {result.errors.length > 0 ? (
        <ErrorNotice>
          {t(strings.errorsHeading, { count: fmt(result.errors.length) })}{" "}
          {result.errors
            .slice(0, 5)
            .map((error) =>
              t(strings.errorLine, {
                line: fmt(error.line),
                message: error.message,
              })
            )
            .join(" · ")}
        </ErrorNotice>
      ) : null}
      {result.includes.length > 0 ? (
        <Notice>
          {tPlural(strings.includesNote, locale, result.includes.length, {
            count: fmt(result.includes.length),
            files: result.includes.join(", "),
          })}
        </Notice>
      ) : null}
      {result.subcounts.length > 1 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.sectionsHeading}</h3>
          <DataTable
            head={[
              strings.colSection,
              strings.colText,
              strings.colHeaders,
              strings.colOther,
            ]}
            rows={result.subcounts.map((sub) => [
              sub.title === "_top_" ? strings.beforeFirst : sub.title,
              fmt(sub.words),
              fmt(sub.headerWords),
              fmt(sub.otherWords),
            ])}
          />
        </div>
      ) : null}
      {result.frequencies.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.frequentHeading}</h3>
          <DataTable
            head={[strings.colWord, strings.colCount]}
            rows={result.frequencies
              .slice(0, TOP_WORDS)
              .map(([word, count]) => [word, fmt(count)])}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.previewHeading}</h3>
        <p className={HINT}>
          <span className="rounded bg-sky-200/70 px-1 dark:bg-sky-400/30">
            {strings.legendText}
          </span>{" "}
          <span className="rounded bg-violet-200/70 px-1 dark:bg-violet-400/30">
            {strings.legendHeader}
          </span>{" "}
          <span className="rounded bg-amber-200/70 px-1 dark:bg-amber-400/30">
            {strings.legendOther}
          </span>
        </p>
        <HighlightedText
          label={strings.previewLabel}
          matches={matches}
          text={source.slice(0, PREVIEW_LIMIT)}
          tones={TONES}
        />
        {source.length > PREVIEW_LIMIT ? (
          <p className={HINT}>
            {t(strings.previewCut, { count: fmt(PREVIEW_LIMIT) })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
